import {TranslationObject} from '@colonist/i18n'
import {GameColor} from '../../../../shared/scripts/GameColor'
import {GameFont} from '../../../../shared/scripts/GameFont'
import {TextView} from '../../../../shared/scripts/ui_canvas_engine/TextView'
import {OpacityAnimationWrapper} from '../../../../shared/scripts/ui_game/ui_game_animations/OpacityAnimationWrapper'
import {GameEndTimeAndTurnsView} from '../../../../shared/scripts/ui_game/ui_game_end_screen/GameEndTimeAndTurnsView'
import {UIGameEndShadeWithLineController} from '../../../../shared/scripts/ui_game/ui_game_end_screen/UIGameEndShadeWithLineController'
import {HideableEndGameSection, UIGameEndController} from '../../../../shared/scripts/ui_game/ui_game_end_screen/UIGameEndController'
import {UIMGameEndStatsControllerBottom} from './UIMGameEndStatsControllerBottom'
import {UIMGameEndStatsControllerTop} from './UIMGameEndStatsControllerTop'
import {UIGameRankedEndController} from '../../../../shared/scripts/ui_game/ui_game_end_screen/UIGameRankedEndController'
import {GameStateEnums} from '../../../../../shared/GameEnums'
import {PIXI} from '@colonist/pixi'
import {StorageWrapper} from '../../../../shared/scripts/StorageWrapper'
import {TimeUtils} from '@colonist/utils'
import {UIGameManager} from '../../../../shared/scripts/ui_game/UIGameManager'
import {EndGameState} from '@colonist/game-states'
import {EndGameRankedUserStateTransferObject} from '@colonist/game-base-types'

export class UIMGameEndController extends UIGameEndController {
    private statsControllerBottom: UIMGameEndStatsControllerBottom
    private shadeWithLineBottom: UIGameEndShadeWithLineController

    constructor(uiGameManager: UIGameManager, endGameState: EndGameState, isReplayAvailable: boolean, rankedUserStates: EndGameRankedUserStateTransferObject[] | undefined, isShownAfterLiveGame: boolean | undefined) {
        super(uiGameManager, endGameState, isReplayAvailable, rankedUserStates, isShownAfterLiveGame)

        this.build()
    }

    protected extraBuildSteps() {
        if(!this.mainContainer) return

        this.animate()
        this.hideChatAndLogs()
        this.setCloseTooltipOnContainers()

        this.mainContainer.interactive = true
        this.mainContainer.on('tap', (e: PIXI.FederatedEvent) => {
            e.stopPropagation()
        })
    }

    onEvent(data: any, eventType: GameStateEnums.UIGameEventEnums) {
        switch(eventType) {
            case GameStateEnums.UIGameEventEnums.CanvasResized: this.onCanvasResized(); break
            default: break
        }
    }

    private onCanvasResized() {
        this.destroy()
        this.returnToMapButton.button?.destroy()
        this.build()
    }

    private setCloseTooltipOnContainers() {
        if(!this.mainContainer) return

        this.mainContainer.on('pointerdown', (e) => {
            this.uiGameManager.toolTipController.closeTip()
        })
        this.returnToMapButton.button?.on('pointerdown', (e) => {
            this.uiGameManager.toolTipController.closeTip()
        })
    }

    private hideChatAndLogs() {
        this.uiGameManager.eventController.sendEvent(false, GameStateEnums.UIGameEventEnums.ToggleChatFeed)
        this.uiGameManager.eventController.sendEvent(false, GameStateEnums.UIGameEventEnums.ToggleLogFeed)
    }

    protected createTitle() {
        const y = this.sectionSize * 0.65
        const text = this.getEndGameText()
        const color = GameColor.getColor(GameColor.Color.Gray3)

        this.title = new TextView(text, 0, y, this.contentContainer.width, this.sectionSize, this.contentContainer, GameFont.Size.XXXL, color, false)
        this.title.makeBold()
        this.title.centerXInParent()
    }

    protected createTimeAndTurns() {
        const y = this.title.y + this.title.height
        this.timeTurns = new GameEndTimeAndTurnsView(0, y, this.contentContainer.width,
            this.sectionSize * 0.25, this.contentContainer, this.endGameState)
    }

    protected createReturnToMapButton() {
        const helpButtonsX = this.uiGameManager.canvasController.appW * 0.01
        const helpButtonsY = (StorageWrapper.getStatusBarHeight() ?? 48) + 12
        const helpButtonSize = this.uiGameManager.canvasController.appW * 0.075
        const spaceBetweenHelpButtons = this.uiGameManager.canvasController.appW * 0.015

        const x = helpButtonsX
        const y = helpButtonsY - helpButtonSize * 0.2
        const w = helpButtonSize * 2 + spaceBetweenHelpButtons
        const h = helpButtonSize * 1.4
        const mapText: TranslationObject = {key: 'strings:game.endGame.cornerButton.returnToMapMobile'}
        const statsText: TranslationObject = {key: 'strings:game.endGame.cornerButton.openStatsMobile'}
        this.returnToMapButton.createButton(x, y, w, h, mapText, statsText)
    }

    // eslint-disable-next-line class-methods-use-this
    protected createViewReplayButton() {
        return
    }

    // eslint-disable-next-line class-methods-use-this
    protected createShareButton() {
        return
    }

    protected createStatsController() {
        if(!this.mainContainer) return

        const topStatsY = this.timeTurns.y + this.timeTurns.height + this.mainContainer.height * 0.03
        const statsH = this.mainContainer.height * 0.33
        this.statsController = new UIMGameEndStatsControllerTop(this.uiGameManager, this, topStatsY, statsH, this.contentContainer, this.endGameState)

        const bottomStatsY = this.statsController.container.y + this.statsController.container.height
        const footerH = this.mainContainer.height * 0.08
        const bottomStatsH = this.mainContainer.height - bottomStatsY - footerH
        this.statsControllerBottom = new UIMGameEndStatsControllerBottom(this.uiGameManager, this, bottomStatsY, bottomStatsH, this.contentContainer, this.endGameState)
    }

    protected createFooter() {
        if(!this.mainContainer) return

        const margin = this.mainContainer.height * 0.01
        const footerY = this.statsControllerBottom.container.y + this.statsControllerBottom.container.height + margin
        const footerH = Math.min(this.uiGameManager.canvasController.itemSize * 1.5, this.mainContainer.height - footerY)
        this.footerController.build(footerY, footerH, this.mainContainer)
    }

    protected override createShadeWithLine() {
        super.createShadeWithLine()

        if(!this.mainContainer) return

        const canvas = this.uiGameManager.canvasController
        const y = this.statsControllerBottom.container.y + this.statsControllerBottom.tabButtonsView.height
        const h = this.mainContainer.height - y
        this.shadeWithLineBottom = new UIGameEndShadeWithLineController(canvas, y, h, this.mainContainer)
        this.mainContainer.addChildAt(this.shadeWithLineBottom.container, 2)
    }

    protected createEndgameRankedScreen() {
        if(!this.statsController) return

        const myRankedState = this.getPlayerRankedState(this.uiGameManager.gameController.myColor)
        if(myRankedState == undefined) return

        const viewsToHide: HideableEndGameSection[] = [this.statsController, this.shadeWithLine, this.statsControllerBottom, this.shadeWithLineBottom]
        new UIGameRankedEndController(this.uiGameManager, this.contentContainer, this.sectionSize, viewsToHide, this.footerController, myRankedState)
    }

    protected animate() {
        if(this.uiGameManager.gameController.isReplay) return
        if(!this.statsController) return

        new OpacityAnimationWrapper(this.contentContainer, this.uiGameManager, 0, 1, 700)
        new OpacityAnimationWrapper(this.title, this.uiGameManager, 0, 1, 300, 800)
        new OpacityAnimationWrapper(this.shadeWithLine.container, this.uiGameManager, 0, 1, 300, 2000)
        new OpacityAnimationWrapper(this.shadeWithLineBottom.container, this.uiGameManager, 0, 1, 300, 2000)
        new OpacityAnimationWrapper(this.statsController.container, this.uiGameManager, 0, 1, 300, 2000)
        new OpacityAnimationWrapper(this.statsControllerBottom.container, this.uiGameManager, 0, 1, 300, 2000)
        if(this.returnToMapButton.button) new OpacityAnimationWrapper(this.returnToMapButton.button, this.uiGameManager, 0, 1, 300, 4000)
        new OpacityAnimationWrapper(this.timeTurns, this.uiGameManager, 0, 1, 300, 4000)
        const myRankedUserState = this.getPlayerRankedState(this.uiGameManager.gameController.myColor)
        if(myRankedUserState == undefined) {
            this.footerController.fadeInButtons(4 * TimeUtils.MILLISECONDS)
        }
    }
}
